import React, {
  useState,
  useEffect,
  ReactNode,
  createRef,
  forwardRef,
  useImperativeHandle
} from 'react'
import { createPortal } from 'react-dom'
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import { StyledDialog } from '.'

type Props = {
  children: ReactNode;
  noOverflow?: boolean;
  isOpen?: boolean;
  onClose: () => void;
}

const Dialog = forwardRef<HTMLDivElement, Props>(
  ({
    children, noOverflow = true, isOpen = true, onClose
  }, ref) => {
    const [showCloseAnim, setShowCloseAnim] = useState(false)
    const modalRef = createRef<HTMLDivElement>()
    const CLOSE_ANIM_LENGTH = 100

    // Waits for the close animation to finish before
    // un-rendering the component
    const handleClose = () => {
      setShowCloseAnim(true)
      setTimeout(() => onClose(), CLOSE_ANIM_LENGTH)
    }

    // This exposes the handleClose logic to any
    // parent component (such as ActionDialog)
    useImperativeHandle(ref, () => ({ close: () => handleClose() } as any))

    useEffect(() => {
      setShowCloseAnim(false)

      const modal = modalRef.current

      if (!!modal && isOpen) {
        modal.focus()
        return disableBodyScroll(modal)
      }

      clearAllBodyScrollLocks()
    }, [modalRef.current, isOpen])

    // Ensures the user can always scroll when the modal is hidden
    useEffect(() => {
      return () => {
        clearAllBodyScrollLocks()
      }
    }, [])

    const Element = (
      <StyledDialog ref={ref} noOverflow={noOverflow} isOpen={isOpen} showCloseAnim={showCloseAnim}>
        <dialog open className="modal">
          <div ref={modalRef} className="modal-content">
            {children}
          </div>
        </dialog>
        <div tabIndex={-1} className="backdrop" onClick={() => handleClose()} />
      </StyledDialog>
    )

    if (!isOpen) {
      return <></>
    }

    // Render the modal outside of the DOM hierarchy of the parent component
    return createPortal(Element, document.getElementById('modal-root') as HTMLElement)
  }
)

export default Dialog
